import React, {useState} from "react"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Switch,
  Select,
  SelectItem,
  Chip,
} from "@heroui/react"
import {
  IconSettings,
  IconUsers,
  IconUserPlus,
  IconAlertTriangle,
  IconTrash,
  IconCheck,
} from "@tabler/icons-react"
import * as TablerIcons from "@tabler/icons-react"
import {Integration, IntegrationType} from "../types"
import {INTEGRATION_TYPE_INFO} from "../data/typeInfo"

interface ManageIntegrationModalProps {
  isOpen: boolean
  onClose: () => void
  integration: Integration
  onDisable: (id: string) => void
}

export function ManageIntegrationModal({
  isOpen,
  onClose,
  integration,
  onDisable,
}: ManageIntegrationModalProps) {
  const [monitoringTypes, setMonitoringTypes] = useState<Set<IntegrationType>>(
    new Set(integration.types)
  )
  const [userSelection, setUserSelection] = useState<"all" | "specific">("all")
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Get the icon component dynamically
  const IconComponent =
    (TablerIcons as any)[integration.icon] || TablerIcons.IconPlug

  const handleMonitoringToggle = (type: IntegrationType, enabled: boolean) => {
    const newTypes = new Set(monitoringTypes)
    if (enabled) {
      newTypes.add(type)
    } else {
      newTypes.delete(type)
    }
    setMonitoringTypes(newTypes)
  }

  const handleSave = async () => {
    setIsSaving(true)

    // Artificial delay to show loading state
    await new Promise((resolve) => setTimeout(resolve, 1500))

    // In a real implementation, this would save the monitoring settings
    console.log("Saving integration settings:", {
      integrationId: integration.id,
      monitoringTypes: Array.from(monitoringTypes),
      userSelection,
    })

    setIsSaving(false)
    onClose()
  }

  const handleDisableConfirm = () => {
    onDisable(integration.id)
    setShowDisableConfirm(false)
    onClose()
  }

  if (showDisableConfirm) {
    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={() => {
          setShowDisableConfirm(false)
          onClose()
        }}
        size="md"
        classNames={{
          base: "bg-white",
          backdrop: "bg-black/50",
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-2 pb-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50">
                <IconAlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Disable Integration
                </h3>
                <p className="text-sm text-gray-500">
                  This action cannot be undone
                </p>
              </div>
            </div>
          </ModalHeader>

          <ModalBody className="py-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <div
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
                  style={{backgroundColor: `${integration.color}15`}}
                >
                  <IconComponent size={16} style={{color: integration.color}} />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium text-red-900">
                    {integration.name}
                  </h4>
                  <p className="mt-1 text-sm text-red-700">
                    Data from this integration will no longer be archived.
                    Existing archived data will remain accessible, but new
                    messages, files, and activities will not be captured.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600">
              Are you sure you want to disable this integration? You can
              reconnect it later, but any data generated while disconnected will
              not be recoverable.
            </p>
          </ModalBody>

          <ModalFooter className="gap-2">
            <Button
              variant="flat"
              onPress={() => setShowDisableConfirm(false)}
              className="text-gray-600"
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={handleDisableConfirm}
              startContent={<IconTrash size={16} />}
            >
              Disable Integration
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: "bg-white",
        backdrop: "bg-black/50",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-2 pb-2">
          <div className="flex items-center gap-3">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-lg"
              style={{backgroundColor: `${integration.color}15`}}
            >
              <IconComponent size={24} style={{color: integration.color}} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-gray-900">
                  {integration.name}
                </h3>
                <Chip
                  size="sm"
                  color="success"
                  variant="flat"
                  startContent={<IconCheck size={12} />}
                >
                  Connected
                </Chip>
              </div>
              <p className="text-sm text-gray-500">{integration.description}</p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="gap-6">
          {/* Monitoring Settings */}
          <div>
            <h4 className="mb-3 text-lg font-semibold text-gray-900">
              Monitoring Settings
            </h4>
            <p className="mb-4 text-sm text-gray-600">
              Select which types of data you want to monitor and archive from
              this integration.
            </p>
            <div className="space-y-3">
              {integration.types.map((type) => (
                <div
                  key={type}
                  className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {INTEGRATION_TYPE_INFO[type]?.name || type}
                    </div>
                    <div className="text-sm text-gray-500">
                      {INTEGRATION_TYPE_INFO[type]?.description ||
                        `Monitor ${type.toLowerCase()} activities`}
                    </div>
                  </div>
                  <Switch
                    isSelected={monitoringTypes.has(type)}
                    onValueChange={(enabled) =>
                      handleMonitoringToggle(type, enabled)
                    }
                    color="primary"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* User Requirements */}
          <div>
            <h4 className="mb-3 text-lg font-semibold text-gray-900">
              Required For
            </h4>
            <p className="mb-4 text-sm text-gray-600">
              Specify which users or groups require access to this integration.
            </p>

            <div className="space-y-3">
              <div
                className={`cursor-pointer rounded-lg border p-4 transition-colors ${
                  userSelection === "all"
                    ? "border-blue-300 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setUserSelection("all")}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                    <IconUsers size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">All Users</div>
                    <div className="text-sm text-gray-500">
                      All organization members have access to this integration
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div
                      className={`h-4 w-4 rounded-full border-2 ${
                        userSelection === "all"
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      }`}
                    >
                      {userSelection === "all" && (
                        <div className="flex h-full w-full items-center justify-center">
                          <div className="h-1.5 w-1.5 rounded-full bg-white"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="cursor-not-allowed rounded-lg border border-gray-200 p-4 opacity-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                    <IconUserPlus size={20} className="text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-500">
                      Specific Users & Groups
                    </div>
                    <div className="text-sm text-gray-400">
                      Choose specific users or groups (Feature coming soon)
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="h-4 w-4 rounded-full border-2 border-gray-300"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="mb-3 text-lg font-semibold text-red-900">
              Danger Zone
            </h4>
            <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex-1">
                <div className="font-medium text-red-900">
                  Disable Integration
                </div>
                <div className="text-sm text-red-700">
                  Stop archiving data from this integration. If you disable and
                  re-enable the integration, your users will need to
                  re-authenticate.
                </div>
              </div>
              <Button
                color="danger"
                variant="flat"
                onPress={() => setShowDisableConfirm(true)}
                startContent={<IconTrash size={16} />}
                className="flex-shrink-0"
              >
                Disable
              </Button>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="gap-2">
          <Button
            variant="flat"
            onPress={onClose}
            className="text-gray-600"
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            startContent={<IconSettings size={16} />}
            isLoading={isSaving}
            isDisabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
